import {
  Award,
  Review,
  StorefrontImage,
  TastingNote,
  UrgencyMessage,
  WineAttributes,
} from "../../types";
import { Box, Heading, Section, Stack, Text } from "@vinomofo/components";

import { InlineAddToCart } from "../inline-add-to-cart";
import { OfferHeroMixed, OfferHeroSingle } from "../offer-hero";
import React from "react";
import { VideoBlock } from "../video-block";
import { VinoDataOffer } from "../../types/vino-data-types";
import { WineNotesSingle } from "../wine-notes";
import { WineReviews } from "../wine-reviews";
import { WineWineNerd } from "../wine-wine-nerd";
import { getExternalUserID } from "../../../api/src/utils/auth";
import { richTextParserRenderer } from "../../utils/rich-text-renderer";
import styled from "styled-components";
import useAuth from "../../hooks/use-auth";

import RelatedProductItems from "../related-offers/RelatedProductItems";
import { isProductionAU } from "../../utils/environment-helpers";
import { RelatedSimilarItems } from "../related-offers/RelatedSimilarItems";
import { OfferDetailsMixedProps } from "./OfferDetailsMixed";
import { WideHeroImageContainer } from "./OfferDetailsMixed";

const Description = styled(Text)`
  * + * {
    margin-top: 1em;
  }
  hr {
    border-top: 1px solid ${({ theme }) => theme.colors.mofoGreen};
    margin-top: 1.5em;
  }
  blockquote + blockquote {
    margin-top: 0;
  }
`;

const StyledBox = styled(Box)`
  margin: 0.5em auto 0;
  h2 {
    margin-top: 1em;
  }
  h3,
  h4 {
    margin-top: 0.8em;
  }
  p {
    margin-top: 0.8em;
  }
`;

export type OfferDetailsSingleProps = {
  productId: string;
  isSpirit?: boolean;
  awards: Array<Award>;
  endsAt?: Date;
  description: any;
  videoUrl;
  videoCaption;
  price: number;
  rrp?: number;
  unitRRP?: number;
  packRRP?: number;
  showCountdown: boolean;
  showPriceDrop: boolean;
  priceDescription?: string;
  shortDescription: string;
  region: string;
  style: string;
  reviews?: Array<Review>;
  tastingNotes: Array<TastingNote>;
  volume: string;
  bottleShape: string;
  wineAttributes: WineAttributes;
  roundelsImage?: StorefrontImage;
  heroImage: StorefrontImage;
  cardImage: StorefrontImage;
  relativeSizedHeroImage: StorefrontImage;
  showDiscountBadge: boolean;
  showAwardBadges: boolean;
  urgencyMessage?: UrgencyMessage;
  isSoldOut?: boolean;
  tagLine?: string;
  subTagLine?: string;
  name: string | undefined;
  bottomDescription: any;
  bottleImage: StorefrontImage | null;
  wideHeroImage: StorefrontImage | null;
  largeHeroImage: StorefrontImage | null;
  hasHeroImages: boolean;
};

export const OfferDetailsSingle = ({
  details,
  offer,
}: {
  details: OfferDetailsSingleProps;
  offer: VinoDataOffer;
}): JSX.Element => {
  const {
    awards,
    description,
    videoUrl,
    videoCaption,
    region,
    reviews = [],
    tastingNotes,
    volume,
    wineAttributes,
    productId,
    isSoldOut,
    bottomDescription,
    isSpirit,
    heroImage,
    wideHeroImage,
    bottleImage,
    name,
    hasHeroImages,
  } = details;
  const { user } = useAuth();
  const userId = getExternalUserID(user);

  return (
    <React.Fragment>
      {hasHeroImages ? (
        <>
          {heroImage && wideHeroImage && (
            <WideHeroImageContainer
              p={0}
              display={["none", "none", "block"]}
              style={{
                backgroundImage: `url(${wideHeroImage?.images?.fallback?.src})`,
              }}
            >
              <OfferHeroMixed
                details={details as OfferDetailsMixedProps}
                offer={offer}
              />
            </WideHeroImageContainer>
          )}
          <Section
            p="0"
            backgroundColor="transparent"
            display={["block", "block", !wideHeroImage ? "block" : "none"]}
          >
            <OfferHeroMixed
              details={details as OfferDetailsMixedProps}
              offer={offer}
            />
          </Section>
        </>
      ) : (
        <OfferHeroSingle details={details} offer={offer} />
      )}
      {isSoldOut &&
        (isProductionAU ? (
          <RelatedProductItems
            indexName={process.env.GATSBY_ALGOLIA_OFFERS_INDEX}
            offerIds={[offer?.id]}
            heading="Don't worry, try one of these"
            subheading=""
            columns={4}
            limit={4}
            userId={userId}
            hidden={offer?.hidden}
            category={offer?.category}
            isSpirit={isSpirit}
          />
        ) : (
          <RelatedSimilarItems
            offerId={offer?.id}
            skuCodes={offer?.skuCode ? [offer?.skuCode] : []}
            userId={userId}
            heading="Don't worry, try one of these"
            limit={4}
          />
        ))}
      <Section py={["50px", "50px", "50px"]} backgroundColor="almostWhite">
        <Stack space={4}>
          <Heading as="h2" variant="xxl">
            Why do we love this{" "}
            {isSpirit ? offer?.category?.toLowerCase() : "wine"}?
          </Heading>
          <Stack space={4}>
            <Description fontSize={["16px", "18px", "20px"]} as="div">
              {richTextParserRenderer(description)}
            </Description>
            {videoUrl && <VideoBlock url={videoUrl} caption={videoCaption} />}
          </Stack>
        </Stack>
      </Section>
      <WineNotesSingle
        awards={awards}
        tastingNotes={tastingNotes}
        bottleImage={bottleImage}
        name={name}
      />
      {reviews?.length > 0 && (
        <WineReviews
          heading="Tell me what you really think."
          subheading="Reviews"
          reviews={reviews}
        />
      )}

      <InlineAddToCart
        productId={productId}
        offer={offer}
        isSoldOut={isSoldOut}
        id={reviews?.length === 0 ? "hero-scroll-stop" : null}
      />

      <WineWineNerd
        heading={`${isSpirit ? `${offer?.category}` : "Wine"} nerd stuff`}
        subheading={`It's cool, we get it, you want to know absolutely everything about this ${
          isSpirit ? `${offer?.category.toLowerCase()}` : "wine"
        }. Well here you go, go nuts.`}
        region={region}
        volume={volume}
        isSpirit={isSpirit}
        {...wineAttributes}
      />

      {bottomDescription ? (
        <Section py={["20px", "40px"]} backgroundColor="white">
          <StyledBox>{richTextParserRenderer(bottomDescription)}</StyledBox>
        </Section>
      ) : null}
      {!isSoldOut &&
        (isProductionAU ? (
          <RelatedProductItems
            indexName={process.env.GATSBY_ALGOLIA_OFFERS_INDEX}
            offerIds={[offer?.id]}
            heading="You might also like…"
            subheading=""
            columns={4}
            limit={4}
            userId={userId}
            hidden={offer?.hidden}
            category={offer?.category}
            isSpirit={isSpirit}
          />
        ) : (
          <RelatedSimilarItems
            offerId={offer?.id}
            skuCodes={offer?.skuCode ? [offer?.skuCode] : []}
            userId={userId}
            heading="You might also like…"
            limit={4}
          />
        ))}
    </React.Fragment>
  );
};
